import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { AuthGuard } from '@/app/guards/AuthGuard';

/* ─── Lazy-loaded pages ─── */
const HomePage = lazy(() => import('@/app/pages/HomePage'));
const SpacesPage = lazy(() => import('@/app/pages/SpacesPage'));
const SpaceDetailPage = lazy(() => import('@/app/pages/SpaceDetailPage'));
const SpaceSettingsPage = lazy(() => import('@/app/pages/SpaceSettingsPage'));
const InventoryPage = lazy(() => import('@/app/pages/InventoryPage'));
const StorageDetailPage = lazy(() => import('@/app/pages/StorageDetailPage'));
const ItemDetailPage = lazy(() => import('@/app/pages/ItemDetailPage'));
const ManifestsPage = lazy(() => import('@/app/pages/ManifestsPage'));
const ManifestDetailPage = lazy(() => import('@/app/pages/ManifestDetailPage'));
const ShipmentsPage = lazy(() => import('@/app/pages/ShipmentsPage'));
const ShipmentDetailPage = lazy(() => import('@/app/pages/ShipmentDetailPage'));
const SettingsPage = lazy(() => import('@/app/pages/SettingsPage'));
const LoginPage = lazy(() => import('@/app/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/app/pages/RegisterPage'));
const InvitePage = lazy(() => import('@/app/pages/InvitePage'));
const NotFoundPage = lazy(() => import('@/app/pages/NotFoundPage'));

/* ─── Route-level Suspense fallback ─── */
function PageFallback() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        blockSize: '50dvh',
        color: 'var(--md-sys-color-on-surface-variant)',
        fontFamily: 'var(--md-sys-typescale-body-font)',
      }}
    >
      Loading…
    </div>
  );
}

/* ─── Router definition ─── */
const router = createBrowserRouter([
  /* Public routes — no AppShell wrapper */
  {
    path: '/login',
    element: (
      <Suspense fallback={<PageFallback />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: '/register',
    element: (
      <Suspense fallback={<PageFallback />}>
        <RegisterPage />
      </Suspense>
    ),
  },
  {
    path: '/invite/:spaceId/:invitationId',
    element: (
      <Suspense fallback={<PageFallback />}>
        <InvitePage />
      </Suspense>
    ),
  },

  /* Protected routes — wrapped in AuthGuard + AppShell layout */
  {
    element: (
      <AuthGuard>
        <AppShell />
      </AuthGuard>
    ),
    children: [
      {
        path: '/',
        element: (
          <Suspense fallback={<PageFallback />}>
            <HomePage />
          </Suspense>
        ),
      },
      {
        path: '/spaces',
        element: (
          <Suspense fallback={<PageFallback />}>
            <SpacesPage />
          </Suspense>
        ),
      },
      {
        path: '/spaces/:spaceId',
        element: (
          <Suspense fallback={<PageFallback />}>
            <SpaceDetailPage />
          </Suspense>
        ),
      },
      {
        path: '/spaces/:spaceId/settings',
        element: (
          <Suspense fallback={<PageFallback />}>
            <SpaceSettingsPage />
          </Suspense>
        ),
      },
      {
        path: '/inventory',
        element: (
          <Suspense fallback={<PageFallback />}>
            <InventoryPage />
          </Suspense>
        ),
      },
      {
        path: '/inventory/:storageId',
        element: (
          <Suspense fallback={<PageFallback />}>
            <StorageDetailPage />
          </Suspense>
        ),
      },
      {
        path: '/inventory/items/:itemId',
        element: (
          <Suspense fallback={<PageFallback />}>
            <ItemDetailPage />
          </Suspense>
        ),
      },
      {
        path: '/manifests',
        element: (
          <Suspense fallback={<PageFallback />}>
            <ManifestsPage />
          </Suspense>
        ),
      },
      {
        path: '/manifests/:manifestId',
        element: (
          <Suspense fallback={<PageFallback />}>
            <ManifestDetailPage />
          </Suspense>
        ),
      },
      {
        path: '/shipments',
        element: (
          <Suspense fallback={<PageFallback />}>
            <ShipmentsPage />
          </Suspense>
        ),
      },
      {
        path: '/shipments/:shipmentId',
        element: (
          <Suspense fallback={<PageFallback />}>
            <ShipmentDetailPage />
          </Suspense>
        ),
      },
      {
        path: '/settings',
        element: (
          <Suspense fallback={<PageFallback />}>
            <SettingsPage />
          </Suspense>
        ),
      },
      {
        path: '*',
        element: (
          <Suspense fallback={<PageFallback />}>
            <NotFoundPage />
          </Suspense>
        ),
      },
    ],
  },
]);

/**
 * Application router with auth-protected routes.
 */
export function Router() {
  return <RouterProvider router={router} />;
}
